import type {FC} from 'react';
import {memo, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {toast} from 'react-toastify';

import NiceModal from '@ebay/nice-modal-react';
import {autorun} from 'mobx';
import {Observer} from 'mobx-react';

import type {ChannelApiModel as Channel} from '@yourcoach/shared/api/channel';

import {useI18n} from '@yourcoach/i18n';
import {eventStore} from '@yourcoach/shared/api/event';
import {rewardProfileStore} from '@yourcoach/shared/api/gamification';
import AlarmIcon from '@yourcoach/shared/assets/icons/alarm.svg';
import HelpIcon from '@yourcoach/shared/assets/icons/help.svg';
import {alert} from '@yourcoach/shared/components/alert';
import {Image} from '@yourcoach/shared/uikit/Image';
import {View} from '@yourcoach/shared/uikit/View';
import {getTierImage} from '@yourcoach/shared/utils/entity/gamification';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '@yourcoach/shared/utils/eventEmitter';
import {getUuid} from '@yourcoach/shared/utils/getUuid';
import {getPriceString} from '@yourcoach/shared/utils/money';

import {CustomToast} from '@src/components/CustomToast/CustomToast';
import {YCToast} from '@src/components/YCToast/YCToast';
import localAppStore, {
  getExcludedEvents,
  POST_EVENTS,
} from '@src/context/appStore';
import {getSupportedEvents} from '@src/modules/Chats/context/useChatsLocalStore';
import {routerPath} from '@src/routes/path';
import {NotificationsModal} from '@src/v2/modules/notifications/components/Modal';

import TwoColumns from '../TwoColumns/TwoColumns';

import styles from './styles.module.css';

interface Props {}

const NotificationTopContainer: FC<Props> = () => {
  const {t} = useI18n();

  const navigate = useNavigate();

  const excludedEvents = useMemo(() => getExcludedEvents(), []);

  useEffect(() => {
    emitter.on(WS_RECEIVE_MESSAGE_EVENT, _handleWsMessage);

    _fetchUnreadEvents();
    localAppStore._fetchUnreadEventsCount();

    return () => {
      emitter.off(WS_RECEIVE_MESSAGE_EVENT, _handleWsMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const dispose = autorun(() => {
      if (localAppStore.isTopNotificationsOpen) {
        NiceModal.show(NotificationsModal);
      } else {
        NiceModal.hide(NotificationsModal);
      }
    });

    return dispose;
  }, []);

  const handleOnClickPointsAdd = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    navigate(routerPath.profile.certification.$buildPath({}));
  };

  const _handleWsMessage = async msg => {
    if (excludedEvents.includes(msg.event.type)) {
      switch (msg.event.type) {
        case 'coach_became_restricted': {
          toast(CustomToast, {
            data: {
              icon: <AlarmIcon />,
              message: t('shared:coach_profile_verified'),
              title: t('shared:congrats'),
            },
          });
          break;
        }
        case 'coach_verification_required': {
          toast(CustomToast, {
            data: {
              icon: <AlarmIcon />,
              title: t('shared:coach_verification_required_short'),
            },
          });
          break;
        }
        case 'coach_verified': {
          toast(CustomToast, {
            data: {
              icon: <AlarmIcon />,
              message: t('shared:coach_verified'),
              title: t('shared:congrats'),
            },
          });
          break;
        }
        case 'membership_proposed': {
          const wasMembershipRequested = !!msg.membership.sign;

          alert.show({
            buttons: [
              {
                label: t('shared:ok'),
                onClick: () => {
                  const practiceSlug = msg.program.expanded_coaches[0].slug;
                  const practiceId = msg.program.expanded_coaches[0]._id;
                  const programSlug = msg.program.slug;
                  const programId = msg.program._id;

                  navigate(
                    routerPath.coachPractice.program.$buildPath({
                      params: {
                        practiceSlugOrId: practiceSlug || practiceId,
                        programSlugOrId: programSlug || programId,
                      },
                    }),
                  );
                },
                scheme: 'secondary',
              },
            ],
            message:
              /**
               * @i18n_parser
               *
               * t('notifications:in_app.membership_proposed.description.invite')
               * t('notifications:in_app.membership_proposed.description.request_accepted')
               */
              t(
                `notifications:in_app.membership_proposed.description.${wasMembershipRequested ? 'request_accepted' : 'invite'}`,
                {
                  actorName: msg.actor.name,
                },
              ),
            title: t('notifications:in_app.membership_proposed.title', {
              courseTitle: msg.program.title,
            }),
          });
          break;
        }
        case 'points_added': {
          if (msg.user_reward.total_points <= 0) {
            return;
          }

          const id = `points_added_${getUuid()}`;
          const qty = msg.user_reward.total_points;

          toast(YCToast, {
            data: {
              onClick: handleOnClickPointsAdd,
              qty: `${t('web-app:RewardNotification.title')}  +${qty}`,
              title: msg.user_reward.title,
            },
            toastId: id,
          });
          break;
        }
        case 'questionnaire_completed': {
          toast(CustomToast, {
            data: {
              icon: <HelpIcon />,
              message: t(
                'notifications:in_app.questionnaire_completed.description',
                {
                  actorName: msg.actor.name,
                },
              ),
              onClick: () => {
                navigate(
                  routerPath.questionnaire.view.status.$buildPath({
                    searchParams: {
                      id: msg.user_questionnaire._id,
                    },
                  }),
                );
              },
              title: t('notifications:in_app.questionnaire_completed.title', {
                questionnaireTitle: msg.user_questionnaire.title,
              }),
            },
          });
          break;
        }
        case 'tier_downgraded': {
          await rewardProfileStore.refresh();

          let description: string | undefined;

          if (rewardProfileStore.rewardProfile) {
            if (rewardProfileStore.rewardProfile.next_billing) {
              description = t(
                'notifications:in_app.tier_downgraded.description.regular',
                {
                  price: getPriceString({price: msg.tier.price}),
                },
              );
            } else if (
              rewardProfileStore.rewardProfile.payment_status === 'trial'
            ) {
              description = t(
                'notifications:in_app.tier_downgraded.description.trial',
              );
            }
          }

          alert.show({
            buttons: [
              {
                label: t('shared:ok'),
                onClick: () => {
                  navigate(routerPath.profile.certification.$buildPath({}));
                },
                scheme: 'primary',
              },
            ],
            message: description,
            title: t('notifications:in_app.tier_downgraded.title', {
              tierTitle: msg.tier.title,
            }),
          });
          break;
        }
        case 'tier_upgraded': {
          await rewardProfileStore.refresh();

          let description: string | undefined;

          if (rewardProfileStore.rewardProfile) {
            if (rewardProfileStore.rewardProfile.next_billing) {
              description = t(
                'notifications:in_app.tier_upgraded.description.regular',
                {
                  price: getPriceString({price: msg.tier.price}),
                },
              );
            } else if (
              rewardProfileStore.rewardProfile.payment_status === 'trial'
            ) {
              description = t(
                'notifications:in_app.tier_upgraded.description.trial',
              );
            }
          }

          alert.show({
            beforeTitleSlot: () => (
              <View
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBlockEnd: 20,
                }}
              >
                <Image
                  src={getTierImage(msg.tier)}
                  style={{height: 100, width: 100}}
                />
              </View>
            ),
            buttons: [
              {
                label: t('shared:ok'),
                onClick: () => {
                  navigate(routerPath.profile.certification.$buildPath({}));
                },
                scheme: 'primary',
              },
            ],
            confetti: true,
            message: description,
            title: t('notifications:in_app.tier_upgraded.title', {
              tierTitle: msg.tier.title,
            }),
          });
          break;
        }
        default:
          break;
      }
    }

    if (!excludedEvents.includes(msg.event.type)) {
      localAppStore._fetchUnreadEventsCount();
    }

    const supportedEvents = getSupportedEvents();

    if (POST_EVENTS.concat(supportedEvents).includes(msg.event.type)) {
      _fetchUnreadEvents();
    }
  };

  const _fetchUnreadEvents = async () => {
    await eventStore.fetchUnread({
      limit: 500,
    });

    let mainChatNotify = 0;
    let supportChatNotify = 0;

    eventStore.unreadEvents.forEach(event => {
      if (event.type === 'post_created') {
        // @ts-expect-error: TODO: fix this
        const channel = event.contexts.find(
          (item: any) => item && item._id.split(':')[0] === 'channel',
        ) as Channel | undefined;

        if (channel) {
          if (channel.type === 'support') {
            supportChatNotify += 1;
          } else {
            mainChatNotify += 1;
          }
        }
      }
    });

    localAppStore.setPostCreatedNotification(mainChatNotify);
    localAppStore.setSupportPostCreatedNotification(supportChatNotify);
  };

  return (
    <Observer>
      {() => (
        <>
          <TwoColumns allHeight={false}>
            <div className={styles.NotificationsTopContainer} />
          </TwoColumns>
        </>
      )}
    </Observer>
  );
};

export default memo(NotificationTopContainer);
